import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import type { RaspadinhaParticipant } from '@/types/raspadinha-campaign'
import type { RaspadinhaCampaignWinnerRow } from '@/lib/raspadinha-campaigns'
import layoutStyles from '@/layouts/CampanhasLayout/CampanhasLayout.module.css'
import { WinnerDetailPanel } from './WinnerDetailPanel'
import styles from './WinnerDetailModal.module.css'

type WinnerDetailModalProps = {
  isOpen: boolean
  winnerRow: RaspadinhaCampaignWinnerRow | null
  participant: RaspadinhaParticipant | null
  onClose: () => void
}

export function WinnerDetailModal({
  isOpen,
  winnerRow,
  participant,
  onClose,
}: WinnerDetailModalProps) {
  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen || !winnerRow || typeof document === 'undefined') return null

  const winnerName = winnerRow.winner.name.trim() || 'Ganhador'

  return createPortal(
    <div
      className={layoutStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="winner-detail-modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        className={`${layoutStyles.modalCard} ${styles.modalCard}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={layoutStyles.modalHeader}>
          <div>
            <h2 id="winner-detail-modal-title" className={layoutStyles.modalTitle}>
              {winnerName}
            </h2>
            <p className={layoutStyles.modalSubtitle}>
              Informações completas do ganhador e da contemplação vinculada.
            </p>
          </div>
          <button
            type="button"
            className={layoutStyles.modalClose}
            onClick={onClose}
            aria-label="Fechar detalhes do ganhador"
          >
            <Icon icon="mdi:close" aria-hidden />
          </button>
        </div>

        <div className={layoutStyles.modalBody}>
          <WinnerDetailPanel
            winner={winnerRow.winner}
            slotTime={winnerRow.slotTime}
            dateLabel={winnerRow.dateLabel}
            slotIsActive={winnerRow.slotIsActive}
            participant={participant}
          />
        </div>

        <div className={layoutStyles.modalFooter}>
          <button type="button" className={layoutStyles.modalPrimaryButton} onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
